import React, { useEffect, useState } from 'react';
import { ShoppingCart, Menu, X, LayoutDashboard } from 'lucide-react';
import { Link } from 'react-router-dom';
import { WhatsAppIcon } from './Effects';
import { Button } from './ui/button';
import { useStore } from '../context/StoreContext';

const navItems = [
  { label: 'Home', href: '#home' },
  { label: 'Products', href: '#products' },
  { label: 'Combos', href: '#combos' },
  { label: 'Reviews', href: '#reviews' },
  { label: 'Founders', href: '#founders' },
  { label: 'Contact', href: '#contact' },
];

const Header = ({ onCartClick }) => {
  const { cartCount, isStaff } = useStore();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-border' : 'bg-white/70 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a href="#home" className="flex items-center gap-2">
            <img src="/assets/logo.png" alt="FBD Cleaning" className="h-11 md:h-14 w-auto object-contain" />
          </a>

          <nav className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="nav-link text-[15px] font-medium text-foreground/80 hover:text-primary"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <button
              onClick={onCartClick}
              className="relative p-2.5 rounded-full hover:bg-secondary transition-colors"
              aria-label="Open cart"
            >
              <ShoppingCart className="h-5 w-5 text-foreground" />
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[#e11d1d] text-white text-[11px] font-bold h-5 w-5 rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>

            {isStaff && (
              <Button asChild variant="outline" className="hidden sm:flex border-primary/40 text-primary hover:bg-primary/5">
                <Link data-testid="header-staff-panel-link" to="/staff/b2c"><LayoutDashboard className="h-4 w-4 mr-1.5" /> Staff Panel</Link>
              </Button>
            )}
            <Button asChild className="btn-shimmer hidden sm:flex bg-primary hover:bg-primary/90 text-white font-semibold">
              <a data-testid="header-get-in-touch-btn" href="#contact"><WhatsAppIcon className="h-4 w-4 mr-1.5" /> Get in Touch</a>
            </Button>

            <button
              className="md:hidden p-2 rounded-full hover:bg-secondary"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Menu"
            >
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-border px-4 py-4 space-y-1">
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              onClick={() => setMobileOpen(false)}
              className="block py-2.5 px-2 rounded-lg text-foreground/80 hover:bg-secondary hover:text-primary font-medium"
            >
              {item.label}
            </a>
          ))}
          <Button asChild className="w-full mt-2 bg-primary hover:bg-primary/90 text-white">
            <a data-testid="mobile-get-in-touch-btn" href="#contact" onClick={() => setMobileOpen(false)}><WhatsAppIcon className="h-4 w-4 mr-1.5" /> Get in Touch</a>
          </Button>
          {isStaff && (
            <Button asChild variant="outline" className="w-full mt-2 border-primary/40 text-primary">
              <Link to="/staff/b2c"><LayoutDashboard className="h-4 w-4 mr-1.5" /> Staff Panel</Link>
            </Button>
          )}
        </div>
      )}
    </header>
  );
};

export default Header;
